/**
 * AdminPayoutAccountsPage — /admin/payout-accounts
 *
 * Phase I admin queue. Read-only + force-refresh only — admins CANNOT
 * approve payout onboarding. Stripe owns that decision. This surface
 * exists so support can:
 *   - See every partner's current payout status.
 *   - Jump to the Stripe dashboard for a given `provider_account_id`.
 *   - Force a refresh (poll Stripe now) when the partner reports
 *     "Stripe says I'm good but the app still shows me in-review".
 *
 * Status filter is a Select of the 7 payout statuses plus "all".
 * Row detail (status_history, requirements) is inline-expandable via
 * a per-row disclosure toggle.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw,
  Loader2,
  Wallet,
  ExternalLink,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PAYOUT_STATUSES = [
  "payout_pending",
  "payout_link_issued",
  "payout_in_review",
  "payout_charges_enabled",
  "payout_payouts_enabled",
  "payout_disabled",
  "payout_link_expired",
];

function label(s) {
  return s.replace(/_/g, " ");
}

/* ------------------------------------------------------------------ */
export default function AdminPayoutAccountsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [refreshingId, setRefreshingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: 1, page_size: 100 };
      if (filter !== "all") params.status = filter;
      const { data } = await api.get("/admin/payout-accounts", { params });
      setRows(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const forceRefresh = async (id) => {
    setRefreshingId(id);
    try {
      const { data } = await api.post(
        `/admin/payout-accounts/${id}/refresh`,
      );
      setRows((prev) => prev.map((r) => (r.id === id ? data : r)));
      toast.success("Refreshed from Stripe.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setRefreshingId(null);
    }
  };

  const counts = useMemo(() => {
    const c = { total: rows.length };
    PAYOUT_STATUSES.forEach((s) => (c[s] = rows.filter((r) => r.status === s).length));
    return c;
  }, [rows]);

  return (
    <div
      style={{ padding: "24px" }}
      data-testid="admin-payout-accounts-page"
    >
      <header style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Wallet size={22} color="#F89623" />
          <h1
            style={{ fontSize: 24, fontWeight: 800, color: "#374751", margin: 0 }}
            data-testid="admin-payout-accounts-heading"
          >
            Payout Accounts
          </h1>
        </div>
        <p style={{ color: "#4b5563", marginTop: 6 }}>
          Stripe Connect Express onboarding — read-only. Stripe controls
          approval; admins can only observe and force a refresh.
        </p>
      </header>

      <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
        <div style={{ minWidth: 240 }}>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="admin-payout-accounts-filter">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses ({counts.total})</SelectItem>
              {PAYOUT_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {label(s)} ({counts[s]})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="admin-payout-accounts-refresh-all"
        >
          {loading ? (
            <Loader2 className="animate-spin" size={16} />
          ) : (
            <>
              <RefreshCw size={16} style={{ marginRight: 6 }} /> Reload
            </>
          )}
        </Button>
      </div>

      {loading ? (
        <div data-testid="admin-payout-accounts-loading">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          data-testid="admin-payout-accounts-empty"
          style={{
            padding: 24,
            background: "#f9fafb",
            borderRadius: 12,
            color: "#6b7280",
          }}
        >
          No payout rows in this bucket.
        </div>
      ) : (
        <div
          style={{
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <table
            style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}
            data-testid="admin-payout-accounts-table"
          >
            <thead>
              <tr style={{ background: "#f9fafb", textAlign: "left" }}>
                <th style={cellHead} aria-label="expander" />
                <th style={cellHead}>Partner user id</th>
                <th style={cellHead}>Status</th>
                <th style={cellHead}>Provider ref</th>
                <th style={cellHead}>Last sync</th>
                <th style={cellHead}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <React.Fragment key={r.id}>
                  <tr
                    style={{ borderTop: "1px solid #e5e7eb" }}
                    data-testid={`admin-payout-accounts-row-${r.id}`}
                  >
                    <td style={{ ...cell, width: 24 }}>
                      <button
                        onClick={() =>
                          setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))
                        }
                        style={{
                          border: 0,
                          background: "transparent",
                          cursor: "pointer",
                          padding: 0,
                        }}
                        aria-label={
                          expanded[r.id] ? "Collapse row" : "Expand row"
                        }
                        data-testid={`admin-payout-accounts-toggle-${r.id}`}
                      >
                        {expanded[r.id] ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </button>
                    </td>
                    <td style={cell}>
                      <code style={{ fontSize: 12 }}>{r.partner_user_id}</code>
                    </td>
                    <td style={cell}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 999,
                          background: "#f3f4f6",
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                        data-testid={`admin-payout-accounts-row-status-${r.id}`}
                      >
                        {label(r.status)}
                      </span>
                    </td>
                    <td style={cell}>
                      {r.provider_account_id ? (
                        <a
                          href={`https://dashboard.stripe.com/connect/accounts/${r.provider_account_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "#0369a1" }}
                          data-testid={`admin-payout-accounts-stripe-link-${r.id}`}
                        >
                          {r.provider_account_id}
                          <ExternalLink
                            size={12}
                            style={{ marginLeft: 4, verticalAlign: "middle" }}
                          />
                        </a>
                      ) : (
                        <span style={{ color: "#6b7280" }}>—</span>
                      )}
                    </td>
                    <td style={cell}>
                      {r.last_stripe_sync_at?.slice(0, 16) || "never"}
                    </td>
                    <td style={cell}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => forceRefresh(r.id)}
                        disabled={refreshingId === r.id}
                        data-testid={`admin-payout-accounts-force-refresh-${r.id}`}
                      >
                        {refreshingId === r.id ? (
                          <Loader2 className="animate-spin" size={14} />
                        ) : (
                          "Force refresh"
                        )}
                      </Button>
                    </td>
                  </tr>
                  {expanded[r.id] && (
                    <tr
                      style={{
                        background: "#fafafa",
                        borderTop: "1px solid #e5e7eb",
                      }}
                      data-testid={`admin-payout-accounts-detail-${r.id}`}
                    >
                      <td colSpan={6} style={{ padding: "12px 16px" }}>
                        <RowDetail row={r} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
function RowDetail({ row }) {
  return (
    <div style={{ display: "grid", gap: 10, color: "#374751" }}>
      <div>
        <strong>disabled_reason:</strong>{" "}
        {row.disabled_reason ? (
          <code>{row.disabled_reason}</code>
        ) : (
          <span style={{ color: "#6b7280" }}>none</span>
        )}
      </div>
      <div>
        <strong>Requirements currently due (raw):</strong>{" "}
        {row.requirements_currently_due?.length ? (
          <code>{row.requirements_currently_due.join(", ")}</code>
        ) : (
          <span style={{ color: "#6b7280" }}>none</span>
        )}
      </div>
      <div>
        <strong>Requirements eventually due (raw):</strong>{" "}
        {row.requirements_eventually_due?.length ? (
          <code>{row.requirements_eventually_due.join(", ")}</code>
        ) : (
          <span style={{ color: "#6b7280" }}>none</span>
        )}
      </div>
      <div>
        <strong>Status history:</strong>
        <ul style={{ margin: "6px 0 0 20px", padding: 0, lineHeight: 1.6 }}>
          {(row.status_history || []).map((h, idx) => (
            <li key={idx}>
              <code style={{ fontSize: 12 }}>
                {h.at?.slice(0, 19)} — {h.status} ({h.source})
              </code>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

const cellHead = {
  padding: "10px 14px",
  fontWeight: 700,
  color: "#374751",
  fontSize: 13,
};
const cell = { padding: "10px 14px", color: "#374751" };
